import { http, defaultConfig, getUniversityUrl } from './http';

export type CreateDormitoryGroupPayload = {
  roomSize: number;
  solitary: boolean;
  dormitoryId: number;
  dormitoryRoomId?: number;
};

export interface CardexStudentEntity {
  studentNumber: string;
  userName: string;
  firstName: string;
  lastName: string;
}

export type UpdateDormitoryGroupPayload = {
  solitary: boolean;
  dormitoryRoomId?: number;

  // used for solitary
  dormitoryId?: number;
  roomSize?: number;

  removeDormitoryRoom?: boolean;
};

export type DeleteMemberPayload = {
  studentNumber: string;
  solitary: boolean;
};

export async function fetchUserDormitory() {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/eskans';
  return (await http.get<{ payload: UserDormitoryEntity }>(url, defaultConfig(baseURL))).data;
}

export async function fetchDormitoryRoomMembers(
  dormitoryId: number,
  blockId: number,
  floorId: number,
  roomId: number
) {
  const baseURL = getUniversityUrl();
  const url = `/rest/users/me/dormitories/solitary/${dormitoryId}/blocks/${blockId}/floors/${floorId}/rooms/${roomId}`;
  return (await http.get<{
    payload: {
      id: number;
      code: number;
      roomSize: number;
      personCount: number;
      eskanDatas: DormitoryRoomMember[]
    }
  }>(url, defaultConfig(baseURL))).data;
}

export const leave = async (payload: DormitoryLeaveEntity) => {
  const baseURL = getUniversityUrl();
  const url = '/rest/me/dormitory-leave';
  return http.post<HttpResponse<any>>(url, payload, defaultConfig(baseURL));
};

export const fetchLeaveReport = async (page = 1, limit = 10) => {
  const baseURL = getUniversityUrl();
  const url = `/rest/me/dormitory-leave?page=${page}&limit=${limit}`;
  return http.get<HttpResponse<DormitoryLeaveEntity[]>>(url, defaultConfig(baseURL));
};

export const fetchAttendaneReport = async (page: number, limit: number = 10, fromDate: string, toDate: string) => {
  const baseURL = getUniversityUrl();
  const url = `/rest/users/me/attendance?page=${page}&size=${limit}&fromDate=${fromDate}&toDate=${toDate}`;
  return http.get<APIResponse<DormitoryAttentanceEntity[]>>(url, defaultConfig(baseURL));
};

export const updateLeaveReport = async (id: number, payload: Partial<DormitoryLeaveEntity>) => {
  const baseURL = getUniversityUrl();
  const url = `/rest/me/dormitory-leave/${id}`;
  return http.put<HttpResponse<any>>(url, payload, defaultConfig(baseURL));
};

export const fetchDormitoryRequests = async () => {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/dormitory-requests';
  return http.get<HttpResponse<DormitoryRequestEntity[]>>(url, defaultConfig(baseURL));
};

export const fetchAvailableDormitories = async () => {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/dormitories';
  return http.get<HttpResponse<IdName[]>>(url, defaultConfig(baseURL));
};

export const createPreRegisterRequest = async (data: { dormitoryId: number; description: string }) => {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/dormitory-requests';
  return http.post(url, data, defaultConfig(baseURL));
};

export function fetchSolitaryBlocks(dormitoryId: number) {
  const baseURL = getUniversityUrl();
  const url = `/rest/users/me/dormitories/solitary/${dormitoryId}/blocks`;
  return http.get<HttpResponse<IdName[]>>(url, defaultConfig(baseURL));
}

export function fetchGroupRoomates() {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/student-groups/roommates';
  return http.get(url, defaultConfig(baseURL));
}

export function fetchSolitaryFloors(dormitoryId: number, blockId: number) {
  const baseURL = getUniversityUrl();
  const url = `/rest/users/me/dormitories/solitary/${dormitoryId}/blocks/${blockId}/floors`;
  return http.get<HttpResponse<DormitoryFloorEntity[]>>(url, defaultConfig(baseURL));
}

export function fetchGroupRooms() {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/dormitories/groups/rooms';
  return http.get<HttpResponse<{
    id: number;
    name: string;
    dormitoryBlocks: DormitoryGroupRoomsBlock[]
  }>>(url, defaultConfig(baseURL));
}

export function fetchRoomsCapacity(dormitoryId: number) {
  const baseURL = getUniversityUrl();
  const url = `/rest/users/me/dormitories/${dormitoryId}/rooms-capacity`;
  return http.get<HttpResponse<DormitoryRoomsCapacityEntity>>(url, defaultConfig(baseURL));
}

export function getValidEntranceDormitory() {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/valid-entrance-dormitory';
  return http.get<HttpResponse<DormitoryValidEntranceResponse>>(url, defaultConfig(baseURL));
}

export function getDormitoryQualify({ preReg }: { preReg?: boolean } = {}) {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/dormitory/qualify' + (preReg ? '?preReg=true' : '');
  return http.get<HttpResponse<DormitoryQualifyResponse>>(url, defaultConfig(baseURL));
}

export function createDormitoryGroup(data: CreateDormitoryGroupPayload) {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/student-groups';
  return http.post<any>(url, data, defaultConfig(baseURL));
}

export function updateDormitoryGroup(data: UpdateDormitoryGroupPayload) {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/student-groups';
  return http.put<any>(url, data, defaultConfig(baseURL));
}

export function removeDormitoryGroup() {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/student-groups';
  return http.delete<any>(url, {
    ...defaultConfig(baseURL),
  });
}

export function removeUserFromGroup(data: DeleteMemberPayload) {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/student-groups/members';
  return http.delete<any>(url, {
    data,
    ...defaultConfig(baseURL),
  });
}

export function fetchDormitoryGroup() {
  const url = '/rest/users/me/student-groups';
  const baseURL = getUniversityUrl();
  return http.get<{ payload: DormitoryGroup }>(url, defaultConfig(baseURL));
}

export function inviteMember(studentNumber: string) {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/student-groups/members';
  return http.post<{ payload: any }>(url, { studentNumber }, defaultConfig(baseURL));
}

export function rejectInvite() {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/student-groups/members';
  return http.put<{ payload: any }>(url, { status: 'REJECTED' }, defaultConfig(baseURL));
}

export function acceptinvite() {
  const baseURL = getUniversityUrl();
  const url = '/rest/users/me/student-groups/members';
  return http.put<{ payload: any }>(url, { status: 'APPROVED' }, defaultConfig(baseURL));
}

export async function fetchUserCardexes() {
  const url = '/rest/users/me/eskans/cardexes';
  const baseURL = getUniversityUrl();
  return (await http.get<{ payload: CardexEntity[] }>(url, defaultConfig(baseURL))).data;
}
